import React from 'react';
import { Box, Button, FormControlLabel, Grid, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { z } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { AntSwitch } from '../../components/antSwitch';
import { mergeFinancialSupportWizard, readFinancialSupportWizard } from './financialSupportProgramWizardStorage';
import {
  FieldLabel,
  formFieldSx,
  helperTextSx,
  sectionTitleSx,
} from '../../styles/formStyles';
import { mediumMaterialIconSx } from '../../styles/iconStyles';
import {
  FinancialSupportWizardSidebar,
  wizardContentCardSx,
  wizardContentGridSx,
  wizardInfoTextSx,
  wizardPageShellSx,
  wizardPageSubtitleSx,
  wizardPageTitleSx,
  wizardPrimaryButtonSx,
  wizardSecondaryButtonSx,
  wizardSidebarGridSx,
  wizardTextActionButtonSx,
} from '../../components/financialSupportWizard';

const stepItems = [
  { id: 1, label: 'Basic Info', completed: true },
  { id: 2, label: 'Contribution Rules', active: true },
  { id: 3, label: 'Plans' },
  { id: 4, label: 'Review & Publish' },
];

const contributionRulesSchema = z.object({
  setupFeeContributionRequired: z.boolean(),
  setupFeePercentage: z.coerce.number().min(0).max(100),
  subscriptionFeeContributionRequired: z.boolean(),
  subscriptionFeePercentage: z.coerce.number().min(0).max(100),
});

export default function CreateFinancialSupportProgramContributionRules() {
  const navigate = useNavigate();
  const wizardData = readFinancialSupportWizard();
  const savedContributionRules = wizardData.contributionRules || {};
  const { handleSubmit, control, watch } = useForm({
    resolver: zodResolver(contributionRulesSchema),
    defaultValues: {
      setupFeeContributionRequired: savedContributionRules.setupFeeContributionRequired ?? true,
      setupFeePercentage: savedContributionRules.setupFeePercentage ?? 20,
      subscriptionFeeContributionRequired: savedContributionRules.subscriptionFeeContributionRequired ?? true,
      subscriptionFeePercentage: savedContributionRules.subscriptionFeePercentage ?? 0,
    },
  });

  const setupFeeContributionRequired = watch('setupFeeContributionRequired');
  const setupFeePercentage = watch('setupFeePercentage');
  const subscriptionFeeContributionRequired = watch('subscriptionFeeContributionRequired');
  const subscriptionFeePercentage = watch('subscriptionFeePercentage');

  const onSubmit = (data) => {
    mergeFinancialSupportWizard({
      contributionRules: data,
    });
    navigate('/financialSupport/create-program/plans');
  };

  return (
    <Box sx={wizardPageShellSx}>
      <Typography className="app-page-title" sx={{ ...wizardPageTitleSx, mb: 4.5 }}>
        Create Financial Support Program
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)} className="material-form">
        <Grid container spacing={3} alignItems="stretch">
          <Grid size={{ xs: 12, md: 3.2, lg: 3 }} sx={wizardSidebarGridSx}>
            <FinancialSupportWizardSidebar steps={stepItems} />
          </Grid>

          <Grid size={{ xs: 12, md: 8.8, lg: 9 }} sx={wizardContentGridSx}>
            <Box sx={wizardContentCardSx}>
              <Typography sx={{ ...sectionTitleSx, mb: 0.5 }}>
                Contribution Rules
              </Typography>
              <Typography sx={{ ...wizardPageSubtitleSx, color: '#666666', mb: 5 }}>
                Configure what percentage of fees the user must pay upfront
              </Typography>

              <Stack spacing={7}>
                <Box>
                  <Controller
                    name="setupFeeContributionRequired"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<AntSwitch checked={field.value} onChange={(event) => field.onChange(event.target.checked)} />}
                        label="Setup Fee Contribution Required"
                        sx={{
                          ml: 0,
                          mr: 0,
                          mb: 2,
                          '& .MuiFormControlLabel-label': {
                            fontSize: 16,
                            lineHeight: '24px',
                            letterSpacing: '0.15px',
                            fontWeight: 700,
                            color: '#1C1B1F',
                          },
                        }}
                      />
                    )}
                  />

                  <FieldLabel label="Setup Fee Percentage" />
                  <Controller
                    name="setupFeePercentage"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        type="number"
                        fullWidth
                        error={!!error}
                        helperText={error ? error.message : ''}
                        sx={{ ...formFieldSx, ...helperTextSx }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          inputProps: { min: 0, max: 100 },
                        }}
                        disabled={!setupFeeContributionRequired}
                      />
                    )}
                  />
                  <Typography sx={wizardInfoTextSx}>
                    Percentage of setup fee user must contribute
                  </Typography>
                </Box>

                <Box>
                  <Controller
                    name="subscriptionFeeContributionRequired"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<AntSwitch checked={field.value} onChange={(event) => field.onChange(event.target.checked)} />}
                        label="Subscription Fee Contribution Required"
                        sx={{
                          ml: 0,
                          mr: 0,
                          mb: 2,
                          '& .MuiFormControlLabel-label': {
                            fontSize: 16,
                            lineHeight: '24px',
                            letterSpacing: '0.15px',
                            fontWeight: 700,
                            color: '#1C1B1F',
                          },
                        }}
                      />
                    )}
                  />

                  <FieldLabel label="Subscription Fee Percentage" />
                  <Controller
                    name="subscriptionFeePercentage"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        type="number"
                        fullWidth
                        error={!!error}
                        helperText={error ? error.message : ''}
                        sx={{ ...formFieldSx, ...helperTextSx }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          inputProps: { min: 0, max: 100 },
                        }}
                        disabled={!subscriptionFeeContributionRequired}
                      />
                    )}
                  />
                  <Typography sx={wizardInfoTextSx}>
                    Percentage of subscription fee user must contribute
                  </Typography>
                </Box>
              </Stack>

              <Box sx={{ mt: 7 }}>
                <Typography sx={{ ...sectionTitleSx, mb: 2 }}>
                  Eligibility Message Preview
                </Typography>
                <Box
                  sx={{
                    borderRadius: '18px',
                    border: '1px solid #76A312',
                    background: 'linear-gradient(180deg, #F5FBEF 0%, #FBFFF6 100%)',
                    minHeight: 240,
                    px: { xs: 3, sm: 6 },
                    py: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                  }}
                >
                  <Box sx={{ maxWidth: 620 }}>
                    <Box sx={{ width: 34, height: 34, borderRadius: '50%', bgcolor: '#4F8506', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2.5 }}>
                      <Box component="span" className="material-symbols-rounded" sx={mediumMaterialIconSx}>priority_high</Box>
                    </Box>
                    <Typography sx={{ fontSize: 18, lineHeight: '28px', letterSpacing: '0px', fontWeight: 700, color: '#1C1B1F', mb: 1.5 }}>
                      Eligibility Requirement
                    </Typography>
                    <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F', mb: 1.25 }}>
                      To be eligible for financial support, you are required to pay:
                    </Typography>
                    <Box component="ul" sx={{ listStylePosition: 'inside', pl: 0, mb: 1.5, mt: 0 }}>
                      {setupFeeContributionRequired && (
                        <Typography component="li" sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F', fontWeight: 700 }}>
                          {setupFeePercentage || 0}% of the Setup Fee
                        </Typography>
                      )}
                      {subscriptionFeeContributionRequired && (
                        <Typography component="li" sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F', fontWeight: 700 }}>
                          {subscriptionFeePercentage || 0}% of the Subscription Fee
                        </Typography>
                      )}
                    </Box>
                    <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' }}>
                      The remaining {Math.max(0, 100 - Math.max(setupFeeContributionRequired ? Number(setupFeePercentage || 0) : 0, subscriptionFeeContributionRequired ? Number(subscriptionFeePercentage || 0) : 0))}% will be covered by the support program.
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', md: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mt: 7 }}>
                <Button
                  onClick={() => navigate('/financialSupport')}
                  sx={wizardTextActionButtonSx}
                >
                  Cancel
                </Button>
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', md: 'auto' } }}>
                  <Button
                    onClick={() => navigate('/financialSupport/create-program')}
                    sx={{ ...wizardSecondaryButtonSx, minWidth: { sm: 160 } }}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    sx={{ ...wizardPrimaryButtonSx, minWidth: { sm: 160 } }}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}
